import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import Dashboard from './pages/Dashboard'
import ProductsManager from './pages/ProductsManager'
import IssuesResolution from './pages/IssuesResolution'
import QCTool from './pages/QCTool'
import NewProductsRequests from './pages/NewProductsRequests'
import ProductsTickets from './pages/ProductsTickets'
import Clarifications from './pages/Clarifications'
import ClarificationsManagement from './pages/ClarificationsManagement'
import ProtectedRoute from './components/ProtectedRoute'
import { AppShell } from './components/layout'
import SandboxConsole from './components/SandboxConsole'
import SessionExpiredModal from './components/SessionExpiredModal'
import { PageSpinner } from './components/ui'
import { useTokenManager } from './services/apiClient'

// Única ruta cargada en diferido: es la que arrastra Recharts (~380 kB), y no
// tiene por qué pesarle a quien nunca abre los dashboards de producción.
const ShopwiseProduction = lazy(() => import('./pages/ShopwiseProduction'))

/**
 * Wrapper that renders a protected page inside the AppShell layout.
 *
 * The page is passed as children, never wrapped in an inline component. An
 * inline `() => <AppShell>...</AppShell>` is a new component type on every
 * render, and App re-renders on every silent token refresh (useTokenManager
 * reads the auth context). React then unmounted and remounted the whole page
 * every ~55 min, wiping searches, filters and open modals mid-work
 * (ticket #86e3ugfyv, 2026-10-30).
 */
function ProtectedPage({ component: Component }) {
  return (
    <ProtectedRoute>
      <AppShell>
        <Component />
      </AppShell>
    </ProtectedRoute>
  )
}

function App() {
  // Initialize token manager for API authentication
  useTokenManager();

  return (
    <>
      <Routes>
        {/* All routes use AppShell layout via ProtectedPage */}
        <Route path="/" element={<ProtectedPage component={Dashboard} />} />

        {/* Protected routes wrapped in AppShell */}
        <Route path="/products" element={<ProtectedPage component={ProductsManager} />} />
        <Route path="/new-products-requests" element={<ProtectedPage component={NewProductsRequests} />} />
        <Route path="/products-tickets" element={<ProtectedPage component={ProductsTickets} />} />
        <Route path="/issues" element={<ProtectedPage component={IssuesResolution} />} />
        <Route path="/qc" element={<ProtectedPage component={QCTool} />} />
        <Route path="/clarifications" element={<ProtectedPage component={Clarifications} />} />
        <Route path="/clarifications-management" element={<ProtectedPage component={ClarificationsManagement} />} />
        <Route
          path="/shopwise-production"
          element={
            <Suspense fallback={<PageSpinner />}>
              <ProtectedPage component={ShopwiseProduction} />
            </Suspense>
          }
        />
      </Routes>
      <SandboxConsole />
      <SessionExpiredModal />
    </>
  )
}

export default App
